import Decimal from 'decimal.js';

import { AugmentedUpdateEntityInput, UpdateEntityInput } from '@/types/schema';
import { AugmentedUpdateAccountInput } from '@/types/schema';
import { UnreachableError } from '@/utils/errors';

import { BUSINESS_ENTITY_TYPES } from '../entities.constants';
import { EntityType } from '../types/EntityType';

export type BusinessEntityRealTppExposureUpdate = {
  enableRealTppTaxExposure?: boolean | undefined;
};

export interface AugmentedPartialSubtypeUpdateInput {
  id: string;
  update?: {
    taxDiscount?: Decimal | undefined | null;
    clearTaxDiscount?: boolean | undefined;
  } & BusinessEntityRealTppExposureUpdate;
  updateDesignerAccount: AugmentedUpdateAccountInput;
}

const BUSINESS_ENTITY_ONLY_UPDATE_FIELDS = [
  'enableRealTppTaxExposure',
] as const satisfies (keyof BusinessEntityRealTppExposureUpdate)[];

export function buildBusinessEntityRealTppExposureUpdate(
  entityType: EntityType,
  enableRealTppTaxExposure: boolean | undefined
): BusinessEntityRealTppExposureUpdate {
  if (
    !BUSINESS_ENTITY_TYPES.includes(entityType) ||
    enableRealTppTaxExposure === undefined
  ) {
    return {};
  }
  return { enableRealTppTaxExposure };
}

/**
 * @description the intent of this function is to allow for a type-agnostic entry point to update entities
 * of all types outside of the bounds of the entity form framework.
 */

export function makeEntityTypeUniversalUpdateInput(
  entityId: string,
  entityType: EntityType,
  updateSubtypeInput: AugmentedPartialSubtypeUpdateInput,
  updateEntityInput?: UpdateEntityInput
): AugmentedUpdateEntityInput {
  const sharedProperties = {
    id: entityId,
    update: updateEntityInput || {},
  };

  const subtypeUpdate = updateSubtypeInput.update ?? {};
  const sanitizedSubtypeUpdate = BUSINESS_ENTITY_TYPES.includes(entityType)
    ? subtypeUpdate
    : Object.fromEntries(
        Object.entries(subtypeUpdate).filter(
          ([key]) =>
            !BUSINESS_ENTITY_ONLY_UPDATE_FIELDS.includes(
              key as (typeof BUSINESS_ENTITY_ONLY_UPDATE_FIELDS)[number]
            )
        )
      );

  const updateEntitySubtypeSharedProperties = {
    ...updateSubtypeInput,
    update: sanitizedSubtypeUpdate,
  };

  switch (entityType) {
    case 'revocable-trust':
      return {
        ...sharedProperties,
        updateRevocableTrust: updateEntitySubtypeSharedProperties,
      };
    case 'irrevocable-trust':
      return {
        ...sharedProperties,
        updateIrrevocableTrust: updateEntitySubtypeSharedProperties,
      };
    case 'slat':
      return {
        ...sharedProperties,
        updateSlatTrust: updateEntitySubtypeSharedProperties,
      };
    case 'grat':
      return {
        ...sharedProperties,
        updateGratTrust: updateEntitySubtypeSharedProperties,
      };
    case 'qprt':
      return {
        ...sharedProperties,
        updateQprtTrust: updateEntitySubtypeSharedProperties,
      };
    case 'ilit':
      return {
        ...sharedProperties,
        updateIlitTrust: updateEntitySubtypeSharedProperties,
      };
    case 'individual-account':
      return {
        ...sharedProperties,
        updateIndividualPersonalAccount: updateEntitySubtypeSharedProperties,
      };
    case 'joint-account':
      return {
        ...sharedProperties,
        updateJointPersonalAccount: updateEntitySubtypeSharedProperties,
      };
    case 'custodial-account':
      return {
        ...sharedProperties,
        updateCustodialPersonalAccount: updateEntitySubtypeSharedProperties,
      };
    case 'retirement-account':
      return {
        ...sharedProperties,
        updateRetirementPersonalAccount: updateEntitySubtypeSharedProperties,
      };
    case 'qualified-tuition-account':
      return {
        ...sharedProperties,
        updateQualifiedTuitionPersonalAccount:
          updateEntitySubtypeSharedProperties,
      };
    case 'clt':
      return {
        ...sharedProperties,
        updateCltTrust: updateEntitySubtypeSharedProperties,
      };
    case 'crt':
      return {
        ...sharedProperties,
        updateCrtTrust: updateEntitySubtypeSharedProperties,
      };
    case 'daf':
      return {
        ...sharedProperties,
        updateDonorAdvisedFund: updateEntitySubtypeSharedProperties,
      };
    case 'private-foundation':
      return {
        ...sharedProperties,
        updatePrivateFoundation: updateEntitySubtypeSharedProperties,
      };

    case 'sole-proprietorship':
      return {
        ...sharedProperties,
        updateSoleProprietorshipBusinessEntity:
          updateEntitySubtypeSharedProperties,
      };

    case 'c-corp':
      return {
        ...sharedProperties,
        updateCcorpBusinessEntity: updateEntitySubtypeSharedProperties,
      };

    case 's-corp':
      return {
        ...sharedProperties,
        updateScorpBusinessEntity: updateEntitySubtypeSharedProperties,
      };

    case 'llc':
      return {
        ...sharedProperties,
        updateLlcBusinessEntity: updateEntitySubtypeSharedProperties,
      };

    case 'lp':
      return {
        ...sharedProperties,
        updateLpBusinessEntity: updateEntitySubtypeSharedProperties,
      };

    case 'gp':
      return {
        ...sharedProperties,
        updateGpBusinessEntity: updateEntitySubtypeSharedProperties,
      };

    case 'insurance-account':
      return {
        ...sharedProperties,
        updateInsurancePersonalAccount: updateEntitySubtypeSharedProperties,
      };

    default:
      throw new UnreachableError({
        case: entityType,
        message: `Unrecognized entity type ${entityType}`,
      });
  }
}
